// Конфигурация, меняемая без релиза. SMY-32
//
// Живёт в общем ядре, потому что её редактирует админка (SMY-98), а исполняет
// сервер: форма настроек и то, что сервер читает, обязаны совпадать.
//
// Значения по умолчанию лежат здесь, в коде. В базе хранятся только
// отклонения от них — так на пустой базе всё работает, а в админке видно,
// что именно отклонено от нормы и требует возврата после аварии.

import type { RatingsConfig } from './ratings.ts'
import { SIZE_KINDS, UNIT_IDS, type EditPolicy, type UnitId, type UnitScope } from './units.ts'
import type { PackOffer } from './ledger.ts'
import { TARIFF_IDS, type TariffId } from './tariffs.ts'
import { isTaskTypeId, type TaskTypeId } from './task-types.ts'

/**
 * Поставщики, к которым продукт ходит напрямую. Их двое, и это решение, а не
 * промежуточное состояние (05.09.2026).
 *
 * Прямой договор со Сбером убран отсюда вместе с идентификатором `gigachat`.
 * Держать в перечислении поставщика, которого нельзя настроить, — то же
 * «третье состояние», что объявленный и неработающий линтер (SMY-105):
 * строка есть, толку нет, а тесты вокруг неё пишутся как вокруг настоящего.
 *
 * Довод, ради которого его хотели сохранить, тоже отпал. Предполагалось, что
 * прямой маршрут сильнее внешнего через Cloud.ru — но тот оказался локально
 * развёрнутым, то есть по контуру данных не слабее прямого. Заменять нечего.
 *
 * Понадобится — вернуть недолго: адаптер строится на общем OpenAI-совместимом
 * клиенте, а черновик с живыми ключами остаётся в истории git.
 */
export const PROVIDER_IDS = ['yandex', 'cloudru'] as const
export type ProviderId = (typeof PROVIDER_IDS)[number]

/**
 * Где физически обрабатываются данные. SMY-79 · тогл прозрачности
 *
 * Не украшение: обещание «Обработка в России» стоит на экране 11 включённым и
 * заблокированным тоглом, и деталь результата обязана говорить то же самое.
 * Таблицей, а не строкой в вёрстке, — чтобы первый же нероссийский маршрут
 * потребовал изменить ЗДЕСЬ, а не оставил обещание в силе по недосмотру.
 */
export const PROVIDER_REGION: Record<ProviderId, string> = {
  yandex: 'Россия',
  cloudru: 'Россия',
}

/**
 * Можно ли называть модель поставщика по имени.
 *
 * ЭТО ДОГОВОРНОЕ ОГРАНИЧЕНИЕ, А НЕ НАСТРОЙКА. Поэтому таблица здесь, в коде,
 * а не в конфигурации: из админки её менять нельзя — меняются не наши
 * предпочтения, а чужие условия, и меняются они чтением договора.
 *
 * * `yandex` — можно. П. 3.11.5 условий запрещает ЛОГОТИПЫ и средства
 *   индивидуализации без согласия; название модели текстом под запрет не
 *   попадает (docs/legal/provider-terms.md). Значки поставщиков в интерфейсе
 *   поэтому и не рисуются — только текст.
 * * `cloudru` — нельзя, и это не про Cloud.ru плохого. Требований к переносу
 *   ограничений мы у них не читали: в разборе условий этот пункт помечен
 *   «⚠️ читать при подключении». Умолчание при непрочитанном договоре —
 *   молчать.
 *
 * Где нельзя, маршрут показывается нейтрально: «российский поставщик» вместо
 * имени. Обещание прозрачности от этого не нарушается — человек по-прежнему
 * видит, что задачу выполнял не мы сами и где это происходило.
 */
export const PROVIDER_NAMEABLE: Record<ProviderId, boolean> = {
  yandex: true,
  cloudru: false,
}

/** Как назвать поставщика, которого называть по имени нельзя. */
export const NEUTRAL_PROVIDER = 'российский поставщик'

/**
 * Сценарии с экрана 02 плюс свободный чат.
 *
 * ⚠️ «Найти» здесь больше нет (06.09.2026). Веб-поиск из продукта не ушёл — он
 * перестал быть отдельным сценарием и стал этапом внутри обычного ответа
 * (`chatWorkflow`). Разбор — в шапке scenarios.ts.
 */
export const SCENARIO_IDS = ['chat', 'write', 'doc_review', 'doc_create', 'image'] as const
export type ScenarioId = (typeof SCENARIO_IDS)[number]

/**
 * Роли моделей, которые сценарий действительно спрашивает у маршрутизатора.
 *
 * Это не описание намерений, а список пар `(сценарий, роль)`, с которыми
 * зовётся `route()`: чат отвечает ролью `chat`, классификатор намерения ходит
 * с `classify`, память проекта — с `embed`, разбор и составление документа
 * начинают с `complex` и заканчивают проверяющим проходом на `chat`.
 *
 * Нужен ровно для одного: понять, какую роль отняло выключение модели.
 * Маршрут задан ПОСТАВЩИКАМИ, и по нему видно только «поставщик жив». Живой
 * Яндекс с выключенной ART для сценария «изображение» мёртв полностью, а
 * проверка этого не замечала — сохранение проходило молча, а запуск падал
 * на середине с «Не получилось, попробуй ещё раз», которое не сработает
 * никогда (08.09.2026, SMY-38).
 *
 * Слова те же, что у `MODEL_ROLES` в каталоге `@smyslika/providers`. Список
 * здесь второй раз потому, что каталог зависит от ядра, а не наоборот, — та
 * же причина, по которой `validateConfig` принимает каталог параметром.
 * Расхождение двух списков ловит тест в пакете провайдеров: там видны оба.
 */
export const SCENARIO_ROLES = {
  chat: ['classify', 'chat', 'embed'],
  write: ['chat'],
  doc_review: ['complex', 'chat'],
  doc_create: ['complex', 'chat'],
  image: ['image'],
} as const satisfies Record<ScenarioId, readonly string[]>

export interface ProviderConfig {
  enabled: boolean
  /**
   * Можно ли отправлять этому поставщику запросы, в которых были персональные
   * данные.
   *
   * Правило 4 из docs/legal/routing-matrix.md: ПДн не уходят в маршруты с
   * неизвестным retention. Маскирование (SMY-40) работает на распознаваемых
   * форматах и полной гарантии не даёт, поэтому запрос, где ПДн БЫЛИ, остаётся
   * подозрительным даже после очистки — и такой маршрут выбирается отдельно.
   */
  personalData: boolean
  /**
   * Потолок обращений в час. Не про нагрузку, а про запас, который нельзя
   * восстановить: резерв с бесплатной или пакетной квотой сгорает за минуты,
   * если основной маршрут отваливается на ретраях, и кончается раньше, чем о
   * нём успевают узнать. Не задан — ограничения нет.
   *
   * Заводился под FusionBrain, от которого 05.09.2026 отказались (SMY-36).
   * Механизм оставлен: он про любой квотируемый маршрут, а не про конкретного
   * поставщика, и понадобится первому же резерву с ограниченным запасом.
   */
  maxPerHour?: number
}

/**
 * Единицы: сколько их даёт тариф и почём докупаются. SMY-57 · P5 Э5.1
 *
 * Лежит в конфигурации целиком. Причина одна и записана в самой задаче:
 * «лимиты придётся пересчитывать после beta». Всё, что придётся
 * пересчитывать, должно меняться из админки — иначе пересчёт упирается в
 * релиз ровно тогда, когда деньги горят.
 *
 * Объёма результата здесь больше нет: он переехал в `scenarioScope`. Пока
 * единиц было шесть, объём и единица совпадали один в один; с одной единицей
 * на вопрос, поиск и проверку — уже нет, а «семь источников» и «пять
 * утверждений» никуда не делись.
 */
export interface UnitsConfig {
  /** Сколько единиц в месяц даёт тариф. Ноль — единица на тарифе недоступна. */
  limits: Record<TariffId, Record<UnitId, number>>
  /**
   * Что можно докупить, когда лимит кончился. Экран 10.
   *
   * В конфигурации, а не в коде: цены пакетов проверяются ценовым тестом в
   * бете (SMY-89) вместе с ценами тарифов, и менять их придётся чаще всего
   * остального.
   */
  packs: PackOffer[]
  /**
   * Доля остатка, ниже которой категория считается заканчивающейся.
   *
   * Порог задан явно и одним числом на всё: разные пороги по единицам
   * означали бы, что «мало» на экране 10 значит разное в соседних строках.
   * Пол в одну единицу добавляет isLowBalance — см. её.
   */
  warnAtShare: number
}

/**
 * Гостевой аккаунт. «Новая регистрация» Э1
 *
 * ⚠️ ГОСТЬ — НЕ ТАРИФ, А СОСТОЯНИЕ АККАУНТА, и раздел поэтому отдельный.
 * Пятый тариф означал бы миграцию `pgEnum`, место в лестнице `TARIFF_RANK`,
 * строку в прайсе, пункт в выдаче из админки и разрез в аналитике — везде,
 * где гостю делать нечего.
 *
 * В конфигурации, а не в коде, по той же причине, что и лимиты: числа здесь
 * первым делом покрутит бета, а релиз ради срока хранения не случится в тот
 * день, когда он нужен.
 */
/**
 * Как подписывается сессия из кабинета. SMY-175
 *
 * Константой, а не строкой в двух местах: по ней сервер решает, какую
 * лестницу показать (`webSignupSteps` против `signupSteps`), и разъехавшись,
 * эти две строки дали бы вебу мобильные пороги — то есть половину тарифа
 * вместо одной задачи. Молча: ошибка выглядела бы как «лестница не
 * срабатывает», а не как опечатка.
 */
export const WEB_DEVICE = 'Веб'

export interface GuestConfig {
  /**
   * Через сколько дней без единого запроса гость удаляется.
   *
   * 90, а не 30: гость — это человек, который ещё не решил. Сезонная задача
   * («подготовить документы к отпуску») возвращает его через два месяца, и
   * потерять его историю за это время значит наказать за то, что он не
   * торопился. Триста шестьдесят пять было бы честнее к нему и хуже к базе.
   *
   * Зарегистрированных срок не касается вовсе: у них есть чем распорядиться
   * своими данными, и удалять их аккаунт за неактивность мы не вправе.
   */
  abandonedAfterDays: number
  /**
   * Сколько аккаунтов уборка забирает за один проход.
   *
   * Потолок существует не ради скорости, а против замка: каскад по гостю с
   * историей — это сотни строк в десятке таблиц, и проход, взявший разом
   * тысячу аккаунтов, встанет поперёк рабочей базы. Недобранное заберёт
   * следующий проход — уборка идёт по расписанию, и торопиться ей некуда.
   */
  purgeBatch: number
  /**
   * Какую долю тарифного потолка получает гость. SMY-160
   *
   * ⚠️ ДОЛЯ, А НЕ СВОЯ ТАБЛИЦА ЧИСЕЛ, и это несущее решение. Гостевой потолок
   * — половина бесплатного, и без бесплатного он не значит ничего: отдельная
   * таблица разошлась бы с сеткой при первой же её правке, и разошлась бы
   * МОЛЧА. Сетку правят из админки часто (09.09.2026 её удвоили целиком), а
   * заметить, что гостю досталось больше зарегистрированного, можно было бы
   * только по счёту от провайдеров.
   *
   * Тюнить при этом есть что: доля — это ручка, которой регулируют давление
   * на регистрацию, и она поедет первой. Ровно поэтому она здесь, а не
   * константой в коде: пересчёт, требующий релиза, не случится в тот день,
   * когда он нужен (SMY-57).
   *
   * Считается ВНИЗ, но не ниже единицы, — см. `guestLimitFor`.
   */
  limitShare: number
  /**
   * На каком по счёту успешном чате просить зарегистрироваться. SMY-161
   *
   * `[4, 6, 9]`: на четвёртом предложение, на шестом второе, на девятом —
   * стена, после которой новых чатов не начать.
   *
   * ⚠️ МАССИВОМ, А НЕ ТРЕМЯ ПОЛЯМИ, и это не вкусовщина. Ступеней должно
   * быть можно сделать две или четыре, не трогая код: бета покрутит это
   * первым делом — здесь давление на регистрацию, а значит и вся воронка.
   * Три поля означали бы, что «убрать вторую просьбу» — это релиз.
   *
   * ПОСЛЕДНЯЯ СТУПЕНЬ И ЕСТЬ СТЕНА. Отдельного числа под неё нет намеренно:
   * два независимых числа однажды разъедутся так, что стена окажется раньше
   * последней просьбы, и человек упрётся в неё, ни разу не услышав, чего от
   * него хотят.
   *
   * Считается УСПЕШНЫМИ ЧАТАМИ, а не запусками: четыре уточнения в одном
   * разговоре — это одна задача, а не четыре, и лестница по запускам
   * выстрелила бы на первом же длинном чате, то есть на самом вовлечённом
   * человеке.
   */
  signupSteps: number[]
  /**
   * То же для ВЕБА, и число здесь другое не по недосмотру. SMY-175
   *
   * `[1]`: на первом же успешном чате и предложение, и стена — потому что
   * последняя ступень и есть стена (см. `signupSteps` выше). Одним числом
   * описывается весь замысел: человек заходит, делает задачу, видит результат
   * — и дальше почта.
   *
   * ⚠️ ПОЧЕМУ НЕ `[4, 6, 9]`, КАК В ПРИЛОЖЕНИИ. Раньше гостя в вебе не было
   * вовсе, и довод был записан прямо: «устройство» здесь — это вкладка, а
   * значит приватное окно даёт нового гостя на каждый заход, и половина
   * бесплатного тарифа раздавалась бы бесконечно. Довод никуда не делся —
   * он снят ЧИСЛОМ: одна задача на приватное окно это цена привлечения, а
   * половина тарифа была бы дырой.
   *
   * Отсюда же следует, что отдельный потолок вебу не нужен: стена на первом
   * чате наступает раньше любой доли тарифа, и второго числа, способного с
   * этим разъехаться, здесь нет.
   */
  webSignupSteps: number[]
  /**
   * Через сколько успешных чатов ПОСЛЕ РЕГИСТРАЦИИ предлагать пароль. SMY-168
   *
   * `[3, 6]`: через три чата предложение, пропустил — ещё через три второе,
   * дальше никогда (остаётся профиль).
   *
   * ⚠️ ТРИ, А НЕ ОДИН, и это заменяет прежнее правило «после первого готового
   * ответа». Обоснование прежнего никуда не делось и остаётся верным — пароль
   * защищает то, что уже нажито, и человеку без единого результата предлагают
   * защитить пустоту. Но теперь перед этим человек прошёл три экрана
   * регистрации, и четвёртый — «а теперь придумайте пароль» — читается как
   * продолжение анкеты, от которой мы обещали избавить.
   *
   * ⚠️ ОТСЧЁТ ОТ РЕГИСТРАЦИИ, а не от начала жизни аккаунта. Гость, дошедший
   * до подтверждения почты на девятом чате, иначе получил бы предложение
   * пароля в ту же минуту — то есть ровно то, чего мы избегали.
   *
   * Ступеней две, а не три: третья просьба об одном и том же — это уже не
   * предложение, а давление, и человек начинает искать, где его выключить.
   */
  passwordSteps: number[]
}

/** Настройки кластеризации запросов. SMY-125 · P8 Э8.6 */
export interface TopicsConfig {
  /** Считать ли темы вообще. Ноль запросов в час — то же, что выключено. */
  enabled: boolean
  /**
   * Косинусная близость, при которой запрос считается той же темой.
   *
   * Единица — совпадение, ноль — ничего общего. Порог крутится по живым
   * данным: слишком низкий сводит «напиши письмо» с «напиши претензию» в одну
   * тему, слишком высокий разводит одну задачу по формулировкам.
   */
  similarity: number
  /**
   * Сколько запросов должно набраться, чтобы тема появилась.
   *
   * Тема из двух запросов — совпадение, а не спрос. Порог тот же по смыслу,
   * что у минимальной выборки метрик гейта: доля на трёх наблюдениях равна
   * 0, 33, 67 или 100 процентам и решением быть не может.
   */
  minSize: number
  /**
   * Сколько запросов считать за один проход.
   *
   * Эмбеддинг стоит денег на каждом запросе, и это названо заранее: без
   * потолка первый же проход по накопленной базе выставит счёт, которого никто
   * не ждал. Расход виден отдельной строкой на дашборде unit-экономики.
   */
  batchPerRun: number
}

/** Выключатель рецептов: разбор у поля `recipes` в `RuntimeConfig`. */
export interface RecipesConfig {
  enabled: boolean
  off: TaskTypeId[]
}

export interface RuntimeConfig {
  providers: Record<ProviderId, ProviderConfig>
  /**
   * Выключенные модели каталога. SMY-98
   *
   * ПЕРЕЧИСЛЯЮТСЯ ОТКЛОНЕНИЯ, А НЕ РАЗРЕШЁННОЕ, и это главное свойство поля.
   * Отсутствие ключа означает «включена»: каталог в `packages/providers` и
   * есть аллоулист, а конфигурация умеет его только СУЗИТЬ. Обратное —
   * список разрешённых в базе — означало бы, что правило 1 матрицы маршрутов
   * («список моделей задаётся конфигом, а не выдачей /models») обходится из
   * админки: дописал строку, и модель в маршруте.
   *
   * Отсюда же и то, что новая модель после релиза работает сразу, никого не
   * дожидаясь. Иначе каждый выпуск требовал бы похода в админку, а забытый
   * поход выглядел бы как «модель не подключилась».
   *
   * Ключ — наш идентификатор из каталога (`yandex/flash`), а не URI
   * провайдера: он же стоит в трейсе, и человек в аварию ищет по нему.
   *
   * Выключить поставщика целиком по-прежнему можно и нужно `providers`:
   * выключение всех его моделей даёт тот же исход, но окольно и молча.
   */
  models: Record<string, { enabled: boolean }>
  scenarios: Record<ScenarioId, { enabled: boolean }>
  /**
   * Кто обслуживает сценарий И В КАКОМ ПОРЯДКЕ. Задаёт маршрутизатор P3.
   *
   * ПОРЯДОК В МАССИВЕ И ЕСТЬ ПРИОРИТЕТ, с 07.09.2026. До этого дня порядок
   * здесь молча выбрасывался: очередь строилась сортировкой по общему на все
   * сценарии числу `providers[id].priority`. То есть массив выглядел
   * упорядоченным, а решало другое поле в другом месте — и «на документы
   * сначала Cloud.ru, а на чат сначала Яндекс» выразить было нечем.
   *
   * Число убрано, а не оставлено «на всякий случай»: поле, которое ни на что
   * не влияет, — то самое третье состояние, и первый же читатель решит, что
   * влияет. Одно понятие вместо двух, и оно посценарное по устройству.
   *
   * Переход прошёл без единой перестановки: на 07.09.2026 порядок в каждом
   * маршруте совпадал с тем, что давала сортировка (проверено на проде).
   */
  routes: Record<ScenarioId, ProviderId[]>
  units: UnitsConfig
  /**
   * Чем списывается сценарий.
   *
   * Настраивается, а не зашито: во время беты бывает нужно переселить
   * сценарий на другую единицу — например, увести «Написать» с вопросов на
   * документы, если выяснится, что люди пишут им договоры. Такое решение
   * принимается по данным недели, а не по расписанию релизов.
   */
  scenarioUnits: Record<ScenarioId, UnitId>
  /**
   * Объём одного результата: сколько ответов, страниц, вариантов.
   *
   * Ключом сценарий, а не единица: чат и «Написать» списываются одной
   * единицей, а объём у них свой. Числа прямо задают себестоимость и потому
   * меняются из админки, а не релизом.
   */
  scenarioScope: Record<ScenarioId, UnitScope>
  /**
   * Веб-поиск внутри ответа. SMY-47, переселён 06.09.2026
   *
   * Отдельной таблицей, а не строкой в `scenarioScope`, и это не мелочь.
   * Объём результата — это то, что обещано пользователю («один ответ на
   * вопрос»), а `sources` — сколько мы читаем, чтобы этот ответ собрать. У
   * чата теперь есть и то и другое, а `UnitScope` умеет ровно одну величину
   * на сценарий — и это его решение, а не ограничение (см. units.ts).
   *
   * Число прямо задаёт себестоимость: семь источников — семь обращений к
   * поисковому API. Отсюда и место в конфигурации, а не в коде.
   */
  web: { sources: number }
  /**
   * Сколько правок результата бесплатны и как долго.
   *
   * Своё у каждого сценария, и это не настройка ради настройки: у изображения
   * правка — новая генерация, и мерить надо время; у документа — число, потому
   * что читают его дольше трёх минут. Разбор в `EditPolicy`.
   */
  freeEdits: Record<ScenarioId, EditPolicy>
  /**
   * Как часто спрашивать оценку результата. SMY-123
   *
   * В конфигурации, а не в коде, потому что это ровно та величина, которую
   * придётся крутить во время беты: просьба оценить после каждого ответа —
   * налог на пользование, а раз в двадцать запусков не наберёт выборки.
   */
  ratings: RatingsConfig
  /**
   * Рецепты задач в постановке модели. SMY-496 · Э1
   *
   * Выключатель без выкладки: компилятор промпта ставит рецепт типа
   * (`@smyslika/core/recipes`) только при `enabled` и только типам, которых нет
   * в `off`. Тип, на котором рецепт портит ответы, снимается одной командой
   * `config/cli.ts disable-recipes ТИП`, остальные остаются; всё разом —
   * `disable-recipes` без типа. Умолчание — выключено: включает владелец после
   * оценки пар Э0/Э1 (решение 23.10.2026, В4А), а не выкладка кода.
   *
   * Отвергнуто: флаг в переменной окружения — его правка требует пересоздания
   * контейнера, а откат плохого рецепта нужен в ту же минуту.
   */
  recipes: RecipesConfig
  /**
   * Кластеризация запросов по смыслу. SMY-125 · P8 Э8.6
   *
   * В конфигурации, а не в коде, потому что крутить это придётся по живым
   * данным: порог близости и минимальный размер темы задают, что вообще
   * считается повторяющейся задачей, — а релиз ради порога не случится в тот
   * день, когда он нужен.
   */
  topics: TopicsConfig
  /** Гостевой аккаунт: срок жизни брошенного и размер прохода уборки. */
  guest: GuestConfig
  budgets: {
    /**
     * Потолок себестоимости на один завершённый результат, по СЦЕНАРИЯМ.
     *
     * Одним числом это не задать: обычный ответ стоит 0,09 ₽, проверка
     * фактов — 5,74 ₽, и общий потолок был бы либо бессмысленным для первого,
     * либо недостижимым для второй.
     *
     * По сценариям, а не по единицам, с 06.09.2026. Слияние лимитов свело
     * чат, поиск и проверку в одну единицу — потолок по единице стал бы одним
     * на все три, то есть равным самому дорогому, и перестал бы ловить
     * разнос дешёвого сценария: чат, ушедший в двадцатикратный перерасход,
     * укладывался бы в бюджет проверки и не сработал бы ни разу.
     *
     * Это предохранитель, а не цель. Нормальный запуск в него не упирается;
     * упирается тот, который пошёл вразнос — зациклился на повторах или
     * получил документ втрое больше ожидаемого.
     */
    perScenario: Record<ScenarioId, number>
  }
}

export const DEFAULT_CONFIG: RuntimeConfig = {
  providers: {
    // Условия прочитаны, обучение отключается заголовком, контур российский.
    yandex: { enabled: true, personalData: true },
    // Второй текстовый поставщик (SMY-35): GigaChat3 10B через Cloud.ru.
    // Модель ОТКРЫТАЯ и развёрнута самим Cloud.ru — не проксируется в Сбер,
    // это видно из карточки модели ("provider": "cloud.ru", "oss": true).
    // То есть маршрут локальный и правило 1 матрицы соблюдается.
    //
    // personalData: true с 05.09.2026, и это не смягчение правила 4, а его
    // выполнение. Правило требует, чтобы retention был ИЗВЕСТЕН, — он известен
    // и прочитан ещё в P0: условия Cloud.ru для локально развёрнутых моделей
    // говорят, что модель «не может обращаться вовне», дообучение не ведётся,
    // данные не пишутся в логи (docs/legal/provider-terms.md). Наш же разбор
    // называет это лучшим российским контуром из четырёх провайдеров —
    // формулировка сильнее, чем у Яндекса, где отключение логирования надо
    // ещё не забыть включить.
    //
    // Неизвестен был не retention, а класс модели: локальная или внешняя.
    // Карточка ответила, вопрос закрылся.
    //
    // Практическое следствие: запрос, в котором были ПДн, теперь переживает
    // аварию Яндекса. Раньше он получал отказ — резерв ему не полагался.
    cloudru: { enabled: true, personalData: true },
  },
  // Пусто, и это не «забыли заполнить». Здесь лежат ОТКЛОНЕНИЯ: нет ключа —
  // модель включена. Перечислить тут весь каталог значило бы завести ему
  // вторую копию в другом пакете, которая разойдётся с первой в день, когда
  // модель добавят и забудут дописать сюда.
  models: {},
  scenarios: {
    chat: { enabled: true },
    write: { enabled: true },
    doc_review: { enabled: true },
    doc_create: { enabled: true },
    image: { enabled: true },
  },
  routes: {
    chat: ['yandex', 'cloudru'],
    write: ['yandex', 'cloudru'],
    doc_review: ['yandex', 'cloudru'],
    doc_create: ['yandex', 'cloudru'],
    // Резерва нет, и это записанное решение, а не пропуск. FusionBrain
    // отброшен 05.09.2026 (SMY-36): сайт недоступен, ключи получить не вышло,
    // а держать в конфигурации поставщика, которого нельзя настроить, — то же
    // самое «третье состояние», что объявленный и неработающий линтер.
    //
    // Следствие честное: правило «два поставщика на критичный сценарий» не
    // выполняется для изображений. Отказ ART роняет сценарий целиком.
    image: ['yandex'],
  },
  // Сетка пересобрана третий раз 09.09.2026 — сначала выставлена В АДМИНКЕ на
  // проде, здесь записана следом.
  //
  // ⚠️ ОТКУДА ЧТО ЧИТАЕТСЯ. Прод берёт `units.limits` из конфигурации в БД, и
  // эти числа — только умолчание: на них живут стенд, тесты и чистая
  // установка. Разойдясь с тем, что выставлено в админке, они не сломают
  // прод — они сделают все наши проверки правдой не про тот продукт, который
  // продаётся. Поэтому админку и этот файл двигают вместе.
  //
  // Лестница стала ровной: каждый следующий тариф вдвое больше предыдущего по
  // вопросам (50 → 100 → 200 → 400) и по документам до «Про» (10 → 20 → 40).
  // Это и было причиной пересборки: подняв бесплатный до 50/10/20, мы сделали
  // прежний «Старт» (50/15/15) бессмысленным — те же вопросы и МЕНЬШЕ
  // изображений, чем даром.
  //
  // Числа названы продуктом, а не выведены из себестоимости, и посчитаны
  // после — economics/cogs.md. Итог расчёта, чтобы он не терялся:
  //
  //   тариф    COGS полной выработки   доля нетто   заработок
  //   Бесплатный        88,67 ₽            —         −88,67 ₽
  //   Старт            177,35 ₽           129%        −39,52 ₽
  //   Плюс             354,70 ₽            77%        106,88 ₽
  //   Про              554,34 ₽            30%       1294,73 ₽
  //
  // ⚠️ ГЕЙТ БОЛЬШЕ ПРОХОДЯТ НЕ ВСЕ, И ЭТО НОВОЕ. Гейт «COGS ≤ 30% нетто»
  // относится к СРЕДНЕЙ выработке по базе; до 09.09 её проходили все четыре с
  // запасом (14,2% / 8,5% / 8,2%). Теперь по средней: «Старт» 32% ❌,
  // «Плюс» 19% ✅, «Про» 7% ✅. То есть «Старт» выпадает уже на средней
  // выработке, а не только на полной, и при полной уходит в минус на 39,52 ₽
  // с подписчика (по потолку — на 177,37 ₽). Разбор — в шапке tariffs.ts.
  //
  // Рычаг прежний — изображения: 40 штук на «Старте» стоят 113,57 ₽ при
  // 137,83 ₽ нетто, то есть 64% себестоимости тарифа и 82% всей выручки с
  // него. Правится отсюда и из админки, без релиза.
  //
  // Бесплатному полагаются документы и изображения, а не ноль: роль тарифа из
  // SMY-61 — «довести человека до первого качественного результата без
  // оплаты». Цена этой роли теперь 88,67 ₽ на человека в месяц при полной
  // выработке и 22,17 ₽ при средней: на 300 приглашённых в бете это от 6,7 до
  // 26,6 тысяч рублей в месяц.
  units: {
    limits: {
      free: { questions: 50, research: 1, documents: 10, images: 20 },
      // Глубокого исследования нет в MVP (см. MVP_UNIT_IDS), и числа под него
      // ОСТАЛИСЬ ПРЕЖНИМИ при пересборке 09.09.2026: админка их не показывает
      // и не трогает, а придумывать лестницу единице, которой в продукте нет,
      // значило бы завести четыре числа, не значащие ничего.
      start: { questions: 100, research: 2, documents: 20, images: 40 },
      plus: { questions: 200, research: 5, documents: 40, images: 80 },
      pro: { questions: 400, research: 20, documents: 100, images: 100 },
    },
    // Три пакета, докупаются на ЛЮБОМ тарифе, включая бесплатный:
    // `checkoutPack` тариф не спрашивает, и это не упущение — пакет нужен
    // тому, у кого лимит кончился, а на каком он тарифе, к делу не относится.
    //
    // ⚠️ СРОКА ЖИЗНИ У ПАКЕТОВ БОЛЬШЕ НЕТ (06.09.2026): `lifetimeMonths` не
    // задан — значит, остаток копится и не сгорает никогда. Прежнее «не
    // сгорает до конца следующего месяца» было и обещанием, и ограничителем:
    // несгоревшее пропадало, и это молча уменьшало наш будущий расход.
    // Теперь весь неиспользованный остаток остаётся нашим обязательством и
    // может лечь в один месяц целиком — на прибыльность не влияет (маржа
    // поштучная), на планирование расхода влияет. Записано осознанно.
    //
    // Экономика пакета при полной выработке (economics/cogs.md):
    //
    //   пакет            нетто     COGS      заработок   маржа
    //   +50 вопросов     91,58 ₽   20,01 ₽    71,57 ₽    78,2%
    //   +25 документов  184,08 ₽   19,21 ₽   164,87 ₽    89,6%
    //   +25 изображений 184,08 ₽   70,98 ₽   113,10 ₽    61,4%
    //
    // Числа стартовые, как и цены тарифов: подтверждаются ценовым тестом в
    // бете (SMY-89), а до тех пор меняются отсюда.
    packs: [
      { unit: 'questions', amount: 50, price: 99 },
      { unit: 'documents', amount: 25, price: 199 },
      { unit: 'images', amount: 25, price: 199 },
    ],
    // 20% — то, что нарисовано на экране 10: «Документы 2 из 15» уже красные,
    // «Вопросы 9 из 10» ещё нет.
    warnAtShare: 0.2,
  },
  // Слияние лимитов живёт здесь: три сценария из шести списываются одной
  // единицей. Именно эта таблица и делает два счётчика одним — не удаление
  // единиц, а то, что «найти» показывает на «вопросы».
  scenarioUnits: {
    chat: 'questions',
    write: 'questions',
    // Разбор и создание списываются с одного лимита — так задумано в тарифах,
    // а не сведено здесь для краткости.
    doc_review: 'documents',
    doc_create: 'documents',
    image: 'images',
  },
  freeEdits: {
    // Ответ в чате правят не правкой артефакта, а следующим сообщением —
    // бесплатных уточнений три (FREE_FOLLOWUPS). Окно здесь для того
    // немногого, что доходит до артефакта: карточки поиска.
    chat: { count: 1, minutes: 10 },
    write: { count: 1, minutes: 10 },
    // Договор читают не три минуты, и окно по времени наказывало бы за
    // внимательность. Три уточнения к одному разбору — часть обещания.
    doc_review: { count: 3 },
    doc_create: { count: 3 },
    // Правка картинки — это новая генерация за 2,84 ₽: поставщик редактировать
    // присланную картинку не умеет (docs/models.md). Поэтому одна и коротко.
    image: { count: 1, minutes: 3 },
  },
  // Числа те же, что раньше жили константами в коде: SOURCES_PER_SEARCH и
  // IMAGE_VARIANTS. Здесь они впервые оказались рядом, и видно, что это одна
  // таблица, а не несвязанные настройки.
  scenarioScope: {
    chat: { size: 1, kind: 'answers' },
    write: { size: 1, kind: 'answers' },
    doc_review: { size: 10, kind: 'pages' },
    doc_create: { size: 10, kind: 'pages' },
    // Один вариант, а не два, с 05.09.2026 — решение про деньги (SMY-108):
    // ART берёт 2,23 ₽ за каждый успешный запрос, и второй вариант удваивал
    // самую дорогую строку себестоимости. Дизайн экрана 08 нарисован на два
    // квадрата; возврат ко второму варианту — это число и поле выбранного
    // варианта в артефакте.
    image: { size: 1, kind: 'variants' },
  },
  // Семь — прежнее обещание карточки «Найти», которое пережило её саму:
  // столько источников продукт читает, когда вопрос требует свежих сведений.
  web: { sources: 7 },
  /**
   * Спрашиваем оценку у каждого пятого запуска. SMY-123
   *
   * Пять — не результат замера, а первая ставка, и её придётся двигать по
   * данным беты: при трёхстах приглашённых это десятки оценок в неделю —
   * достаточно, чтобы разбирать плохие запуски, и редко, чтобы вопрос не
   * читался как назойливость. Первый запуск спрашивается всегда: он же
   * момент активации.
   */
  ratings: { askEveryNth: 5 },
  recipes: { enabled: false, off: [] },
  /**
   * Умолчания кластеризации.
   *
   * ЦЕНА ОЦЕНЕНА ДО ВКЛЮЧЕНИЯ, как и требует SMY-125. Yandex Text Embeddings
   * стоит 0,0101 ₽ за тысячу входных токенов (`packages/providers/catalog.ts`);
   * запрос — сотни токенов, то есть примерно 0,002 ₽ за вектор. Полный проход
   * в 500 запросов — около рубля, а бета на 200–300 человек столько за час не
   * приносит. Поэтому включено по умолчанию: база знаний, выключенная «пока не
   * посчитаем», не включается никогда. Выключатель при этом есть и работает
   * без релиза — на случай, если цифры разойдутся с оценкой.
   *
   * `similarity` 0,82 — не замеренное число, а осторожная стартовая гипотеза:
   * лучше несколько близких тем, которые человек склеит руками (SMY-126), чем
   * одна общая, из которой ничего не следует. Разводить дешевле, чем сводить.
   *
   * `minSize` 3 — минимум, при котором слово «повторяющаяся» вообще применимо.
   */
  topics: { enabled: true, similarity: 0.82, minSize: 3, batchPerRun: 500 },
  // 90 дней — компромисс, разобранный в шапке GuestConfig. Сто аккаунтов за
  // проход при суточном расписании — 36 500 в год, то есть потолок ограничи-
  // вает не объём уборки, а длину одного замка на живой базе.
  // Половина — не подобранное число, а решение: гостю дают попробовать, а не
  // пользоваться. Считается от бесплатного, поэтому при 50/10/20 выходит
  // 25/5/10.
  guest: {
    abandonedAfterDays: 90,
    purgeBatch: 100,
    limitShare: 0.5,
    signupSteps: [4, 6, 9],
    webSignupSteps: [1],
    passwordSteps: [3, 6],
  },
  // Примерно вдвое выше фактической себестоимости из economics/cogs.md:
  // вопрос 0,128 · поиск 0,674 · документ 1,082 · картинка 2,839.
  budgets: {
    perScenario: {
      // Веб-поиск живёт внутри чата, и потолок это учитывает: 0,3 ₽ хватало
      // ответу без источников, но не ответу с ними. Прежний потолок сценария
      // «Найти» был 2 ₽ — берём его, потому что дорожает именно тот запуск,
      // который в веб и пошёл.
      chat: 2,
      write: 0.3,
      doc_review: 3,
      doc_create: 3,
      image: 6,
    },
  },
}

export interface ConfigProblem {
  path: string
  message: string
}

/**
 * Проверка конфигурации перед записью.
 *
 * Главное правило: включённый сценарий обязан иметь хотя бы одного живого
 * поставщика. Смысл флагов в том, чтобы гасить аварию за минуту, — а
 * выключение последнего поставщика при включённом сценарии как раз и есть
 * авария, только устроенная своими руками и молча. Отключить всех можно,
 * но тогда и сценарий выключается явно: пользователь увидит «временно
 * недоступно» вместо ошибки на середине выполнения.
 */
export function validateConfig(
  config: RuntimeConfig,
  /**
   * Каталог моделей: что вообще существует и у кого.
   *
   * Необязателен, и это не послабление. Каталог живёт в `@smyslika/providers`,
   * а тот зависит от ядра — обратный импорт замкнул бы пакеты друг на друга.
   * Второй список идентификаторов здесь, в ядре, был бы ещё хуже: аллоулист
   * получил бы вторую копию, и правило 1 матрицы маршрутов перестало бы иметь
   * один источник.
   *
   * Поэтому каталог ПРИНОСИТ тот, у кого он есть, — сервер при сохранении и
   * админка при просмотре. Кто не принёс, тот проверяет форму, но не имена и
   * не связность по моделям. Связность от этого не расходится между админкой,
   * CLI и сервером: настоящее сохранение идёт одним путём, и каталог туда
   * передаётся.
   */
  catalog?: readonly { id: string; provider: ProviderId; roles: readonly string[] }[],
): ConfigProblem[] {
  const problems: ConfigProblem[] = []

  for (const [id, entry] of Object.entries(config.models ?? {})) {
    if (typeof entry?.enabled !== 'boolean') {
      problems.push({
        path: `models.${id}`,
        message: `Состояние модели «${id}» — да или нет. Чтобы вернуть её к умолчанию, ключ убирают целиком.`,
      })
      continue
    }
    // Опечатка в идентификаторе иначе выглядит как рабочая настройка: тумблер
    // стоит, а не выключает ничего — то самое «третье состояние».
    if (catalog != null && !catalog.some((m) => m.id === id)) {
      problems.push({
        path: `models.${id}`,
        message: `Модели «${id}» нет в каталоге. Настройка может только сузить каталог, а не дополнить его.`,
      })
    }
  }

  for (const scenario of SCENARIO_IDS) {
    if (!config.scenarios[scenario]?.enabled) continue
    const route = config.routes[scenario] ?? []
    // Поставщик, у которого выключены ВСЕ модели, живым не считается: исход у
    // этого тот же, что у выключенного поставщика, — сценарий падает на
    // середине. Проверка одна на оба способа себя подстрелить намеренно;
    // разведи их, и второй способ остался бы без запрета просто потому, что
    // появился позже.
    //
    // Роли здесь не разбираются, и это ровно та же грубость, что была: маршрут
    // задан поставщиками, а не ролями. Поставщик без нужной РОЛИ маршрутизатор
    // пропустит сам (`no_model`), и до сегодняшнего дня это тоже не было
    // ошибкой конфигурации.
    const dead: string[] = []
    const alive = route.filter((p) => {
      if (!config.providers[p]?.enabled) {
        dead.push(`«${p}» выключен`)
        return false
      }
      if (catalog != null && !catalog.some((m) => m.provider === p && modelEnabled(config, m.id))) {
        // Причина названа отдельно не для красоты. Выключают МОДЕЛИ, а сообщение
        // про «поставщиков» отправляет искать не туда — человек только что
        // щёлкал тумблерами в другой таблице и решит, что дело в третьем месте.
        dead.push(`у «${p}» выключены все модели`)
        return false
      }
      return true
    })
    if (alive.length === 0) {
      problems.push({
        path: `scenarios.${scenario}`,
        message:
          `У сценария «${scenario}» не осталось поставщиков: ${dead.join(', ')}. ` +
          'Выключите сам сценарий — иначе он будет падать на середине выполнения.',
      })
      continue
    }

    /**
     * Роль, которую отняло выключение моделей. SMY-38
     *
     * Проверка выше грубая по построению: она спрашивает «остался ли у
     * поставщика хоть один включённый ход», а сценарию нужен ход НУЖНОЙ РОЛИ.
     * Живой Яндекс с выключенной ART проходил её насквозь — текстовые модели
     * при нём, — и «изображение» сохранялось без единого возражения. Дальше
     * маршрутизатор не находил кандидатов (`models_off`), запуск падал
     * исходом `failed`, и человек читал «попробуй ещё раз» там, где повтор не
     * поможет никогда. Ровно тот же класс, что у выключенного сценария, ради
     * которого заведён 503, — только тише.
     *
     * Молчим в двух случаях, и оба намеренно. Роли, которой у маршрута нет в
     * КАТАЛОГЕ, здесь не место: это вопрос к самой матрице маршрутов, а не к
     * тумблерам, и маршрутизатор пропускает такого поставщика сам
     * (`no_model`). И каталог по-прежнему приносит вызывающий: кто не принёс,
     * тот проверяет форму, но не связность.
     */
    if (catalog == null) continue
    for (const role of SCENARIO_ROLES[scenario]) {
      // `?? []` не про типы, а про то, что проверка настройки не имеет права
      // упасть: каталог приносят снаружи, и упавший валидатор хуже
      // недосмотревшего — он не даст сохранить вообще ничего.
      const known = catalog.filter((m) => alive.includes(m.provider) && (m.roles ?? []).includes(role))
      if (known.length === 0 || known.some((m) => modelEnabled(config, m.id))) continue
      problems.push({
        path: `scenarios.${scenario}`,
        message:
          `У сценария «${scenario}» не осталось моделей роли «${role}»: ` +
          `${known.map((m) => `«${m.id}»`).join(', ')} ${known.length === 1 ? 'выключена' : 'выключены'}. ` +
          'Маршрут при этом цел, поэтому запуск не отвергается заранее — он падает на середине. ' +
          'Верните модель или выключите сам сценарий.',
      })
    }
  }

  for (const scenario of SCENARIO_IDS) {
    for (const provider of config.routes[scenario] ?? []) {
      if (!PROVIDER_IDS.includes(provider)) {
        problems.push({ path: `routes.${scenario}`, message: `Неизвестный поставщик: ${provider}` })
      }
    }
  }

  for (const scenario of SCENARIO_IDS) {
    const budget = config.budgets.perScenario?.[scenario]
    // Пропавший сценарий — не мелочь: сохранение частичного объекта затирает
    // остальные, и запуск остался бы вовсе без потолка. Ловим до записи.
    if (!(typeof budget === 'number' && budget > 0)) {
      problems.push({
        path: `budgets.perScenario.${scenario}`,
        message: `Потолок себестоимости для сценария «${scenario}» должен быть числом больше нуля`,
      })
    }
  }
  // Кластеризация. Числа задают, что считается повторяющейся задачей, и
  // мусор в них не «неудачная настройка», а тема из одного запроса на экране.
  const topics = config.topics
  if (!(typeof topics?.similarity === 'number' && topics.similarity > 0 && topics.similarity < 1)) {
    problems.push({
      path: 'topics.similarity',
      message: 'Порог близости — число строго между нулём и единицей. Единица означала бы «только дословные совпадения».',
    })
  }
  if (!(typeof topics?.minSize === 'number' && Number.isInteger(topics.minSize) && topics.minSize >= 2)) {
    problems.push({
      path: 'topics.minSize',
      message: 'Тема начинается минимум с двух запросов: тема из одного — это один запрос.',
    })
  }
  if (!(typeof topics?.batchPerRun === 'number' && Number.isInteger(topics.batchPerRun) && topics.batchPerRun > 0)) {
    problems.push({
      path: 'topics.batchPerRun',
      message: 'Размер прохода — целое больше нуля: эмбеддинг стоит денег на каждом запросе.',
    })
  }

  const guest = config.guest
  if (!(typeof guest?.abandonedAfterDays === 'number' && Number.isInteger(guest.abandonedAfterDays) && guest.abandonedAfterDays > 0)) {
    problems.push({
      path: 'guest.abandonedAfterDays',
      // Ноль означал бы «удалять гостя сразу после запроса», а не «не
      // удалять»: выключателя у этого срока нет намеренно — база, копящая
      // задачи людей, которых мы не можем ни спросить, ни уведомить, это не
      // настройка, а нарушение.
      message: 'Срок жизни брошенного гостя — целое число дней больше нуля.',
    })
  }
  if (!(typeof guest?.purgeBatch === 'number' && Number.isInteger(guest.purgeBatch) && guest.purgeBatch > 0)) {
    problems.push({
      path: 'guest.purgeBatch',
      message: 'Размер прохода уборки — целое больше нуля: нулевой проход не убирает ничего и выглядит как работающая уборка.',
    })
  }
  // Строго больше нуля и не больше единицы. Ноль означал бы гостя без единого
  // вопроса — то есть приложение, которое открывается и ничего не умеет, а
  // человек не понимает, почему. Больше единицы — гостя щедрее
  // зарегистрированного, то есть регистрацию, которая ОТНИМАЕТ.
  if (!(typeof guest?.limitShare === 'number' && guest.limitShare > 0 && guest.limitShare <= 1)) {
    problems.push({
      path: 'guest.limitShare',
      message: 'Доля гостя — больше нуля и не больше единицы. Ноль оставит его без единого вопроса, больше единицы сделает регистрацию потерей.',
    })
  }
  const steps = guest?.signupSteps
  // Строго возрастающие целые больше нуля. Не «на всякий случай»: последняя
  // ступень — это СТЕНА, и порядок здесь решает, упрётся ли человек в неё до
  // того, как ему хоть раз объяснили, чего от него хотят. Пустой список —
  // лестница, которая не срабатывает никогда; выключать её надо не так, а
  // большими числами, иначе «выключено» и «сломано» неразличимы.
  if (
    !Array.isArray(steps) ||
    steps.length === 0 ||
    !steps.every((n, i) => Number.isInteger(n) && n > 0 && (i === 0 || n > steps[i - 1]!))
  ) {
    problems.push({
      path: 'guest.signupSteps',
      message: 'Ступени лестницы — непустой список целых чисел, каждое больше предыдущего. Последняя ступень и есть стена.',
    })
  }
  // Веб проверяется ТЕМ ЖЕ правилом и отдельной строкой, а не вместе с
  // предыдущим: сообщение должно называть, какая из двух лестниц сломана.
  // Склеенная проверка отправила бы читателя искать ошибку не в том числе.
  const webSteps = guest?.webSignupSteps
  if (
    !Array.isArray(webSteps) ||
    webSteps.length === 0 ||
    !webSteps.every((n, i) => Number.isInteger(n) && n > 0 && (i === 0 || n > webSteps[i - 1]!))
  ) {
    problems.push({
      path: 'guest.webSignupSteps',
      message: 'Ступени лестницы в вебе — непустой список целых чисел, каждое больше предыдущего. Последняя ступень и есть стена.',
    })
  }
  // У пароля пустой список ЗАКОННЫЙ — в отличие от лестницы регистрации: там
  // он означал бы стену, которая не наступает никогда, а здесь просто «не
  // предлагать пароль». Это осмысленное состояние: пароль остаётся в профиле.
  const pwSteps = guest?.passwordSteps
  if (
    !Array.isArray(pwSteps) ||
    !pwSteps.every((n, i) => Number.isInteger(n) && n > 0 && (i === 0 || n > pwSteps[i - 1]!))
  ) {
    problems.push({
      path: 'guest.passwordSteps',
      message: 'Ступени предложения пароля — список целых чисел, каждое больше предыдущего. Пустой список означает «не предлагать».',
    })
  }

  for (const provider of PROVIDER_IDS) {
    const limit = config.providers[provider]?.maxPerHour
    if (limit != null && !(limit > 0)) {
      problems.push({
        path: `providers.${provider}.maxPerHour`,
        message: 'Потолок в час должен быть больше нуля. Чтобы закрыть маршрут, выключите поставщика.',
      })
    }
  }

  // Лимиты проверяются по ВСЕМ единицам, а не только по MVP-шным: пропуск
  // здесь — это не «единица пока не работает», а остаток, посчитанный от
  // неизвестно чего. Единица вне MVP просто не запускается сценарием, и ноль
  // в её лимите — законное значение, отсутствие — нет.
  for (const tariff of TARIFF_IDS) {
    for (const unit of UNIT_IDS) {
      const limit = config.units.limits[tariff]?.[unit]
      if (!(typeof limit === 'number' && Number.isInteger(limit) && limit >= 0)) {
        problems.push({
          path: `units.limits.${tariff}.${unit}`,
          message: `Лимит «${unit}» на тарифе «${tariff}» должен быть целым числом не меньше нуля. Ноль означает, что единица на тарифе недоступна.`,
        })
      }
    }
  }

  for (const scenario of SCENARIO_IDS) {
    const scope = config.scenarioScope[scenario]
    if (!(typeof scope?.size === 'number' && Number.isInteger(scope.size) && scope.size > 0)) {
      problems.push({
        path: `scenarioScope.${scenario}.size`,
        message: `Объём результата сценария «${scenario}» должен быть целым числом больше нуля`,
      })
    }
    if (scope != null && !SIZE_KINDS.includes(scope.kind)) {
      problems.push({ path: `scenarioScope.${scenario}.kind`, message: `Неизвестная величина: ${String(scope.kind)}` })
    }
  }

  for (const scenario of SCENARIO_IDS) {
    const edits = config.freeEdits[scenario]
    if (!(Number.isInteger(edits?.count) && edits.count >= 0)) {
      problems.push({
        path: `freeEdits.${scenario}.count`,
        message: `Число бесплатных правок у «${scenario}» — целое не меньше нуля. Ноль означает, что правок нет.`,
      })
    }
    // Отсутствие окна законно — у документа его нет вовсе. А вот ноль или
    // минус означал бы окно, закрытое в момент выдачи: обещание, которого
    // никто не сможет выполнить.
    if (edits?.minutes != null && !(Number.isInteger(edits.minutes) && edits.minutes > 0)) {
      problems.push({
        path: `freeEdits.${scenario}.minutes`,
        message: 'Окно правки — целые минуты больше нуля. Чтобы окна не было, поле не задают.',
      })
    }
  }

  for (const [i, pack] of (config.units.packs ?? []).entries()) {
    if (!UNIT_IDS.includes(pack.unit)) {
      problems.push({ path: `units.packs.${i}.unit`, message: `Неизвестная единица: ${String(pack.unit)}` })
    }
    if (!(Number.isInteger(pack.amount) && pack.amount > 0)) {
      problems.push({ path: `units.packs.${i}.amount`, message: 'Пакет должен добавлять целое число единиц' })
    }
    // Ноль в цене — не «бесплатно», а забытое поле: бесплатное начисление
    // делает админка, и оно проходит другим путём.
    if (!(Number.isInteger(pack.price) && pack.price > 0)) {
      problems.push({ path: `units.packs.${i}.price`, message: 'Цена пакета — целое число рублей больше нуля' })
    }
    // Отсутствие срока законно и с 06.09.2026 — норма: пакет копится и не
    // сгорает. А вот ноль или минус означал бы пакет, сгоревший в момент
    // покупки, — то есть деньги, взятые ни за что.
    if (pack.lifetimeMonths != null && !(Number.isInteger(pack.lifetimeMonths) && pack.lifetimeMonths > 0)) {
      problems.push({
        path: `units.packs.${i}.lifetimeMonths`,
        message: 'Срок жизни пакета — целые месяцы. Чтобы пакет не сгорал, поле не задают.',
      })
    }
  }

  // Ноль источников — не «поиск выключен», а забытое поле: этап пошёл бы в
  // веб и вернулся ни с чем, заплатив за обращение. Выключается поиск
  // отсутствием ключей поисковика, а не нулём здесь.
  const sources = config.web?.sources
  if (!(Number.isInteger(sources) && sources > 0)) {
    problems.push({
      path: 'web.sources',
      message:
        'Сколько источников читать в вебе — целое число больше нуля. ' +
        'Чтобы поиска не было вовсе, убирают ключи поисковика.',
    })
  }

  // Ноль здесь законен — «не спрашивать вовсе», — а вот минус и дробное нет:
  // первое означало бы неопределённый шаг, второе молча округлилось бы, и
  // человек, поставивший 2,5, получил бы не то, что задумал.
  const askEveryNth = config.ratings?.askEveryNth
  if (!(Number.isInteger(askEveryNth) && askEveryNth >= 0)) {
    problems.push({
      path: 'ratings.askEveryNth',
      message:
        'Частота вопроса об оценке — целое число не меньше нуля: спрашивать у каждого N-го запуска. ' +
        'Ноль означает, что оценку не спрашиваем вовсе.',
    })
  }

  // Слаг вне каталога — опечатка в команде: рецепт, который хотели снять,
  // остался бы в постановке, а выключатель отрапортовал бы «снят».
  const recipes = config.recipes
  if (typeof recipes?.enabled !== 'boolean') {
    problems.push({ path: 'recipes.enabled', message: 'Включены ли рецепты задач — да или нет.' })
  }
  const off: unknown = recipes?.off
  if (!Array.isArray(off)) {
    problems.push({ path: 'recipes.off', message: 'Типы задач без рецепта — список.' })
  } else {
    const unknown = off.filter((id) => !isTaskTypeId(id))
    if (unknown.length > 0) {
      problems.push({ path: 'recipes.off', message: `Таких типов задач нет в каталоге: ${unknown.map(String).join(', ')}.` })
    }
    if (new Set(off).size !== off.length) {
      problems.push({ path: 'recipes.off', message: 'Тип задачи назван в списке дважды.' })
    }
  }

  const share = config.units.warnAtShare
  if (!(typeof share === 'number' && share >= 0 && share < 1)) {
    problems.push({
      path: 'units.warnAtShare',
      message: 'Порог предупреждения — доля от нуля до единицы. Единица означала бы «всегда мало».',
    })
  }

  for (const scenario of SCENARIO_IDS) {
    const unit = config.scenarioUnits[scenario]
    if (!UNIT_IDS.includes(unit)) {
      problems.push({
        path: `scenarioUnits.${scenario}`,
        message: `Сценарий «${scenario}» списывается неизвестной единицей: ${String(unit)}`,
      })
    }
  }

  return problems
}

/**
 * Разрешена ли модель к вызову.
 *
 * Отсутствие ключа — «да»: см. шапку поля `models`. Поэтому проверка написана
 * как `!== false`, а не как `=== true`; вторая молча гасила бы весь каталог
 * на пустой конфигурации.
 */
export const modelEnabled = (config: RuntimeConfig, id: string): boolean =>
  config.models?.[id]?.enabled !== false

/**
 * Поставщики сценария в порядке очереди — то, что спросит маршрутизатор.
 *
 * Порядок берётся из `routes[scenario]` как есть. Сортировки здесь больше нет:
 * до 07.09.2026 она перекладывала список по общему числу `priority`, и порядок,
 * заданный для конкретного сценария, до маршрутизатора не доезжал.
 *
 * `personalData` сужает список, а не переупорядочивает его: запрос, в котором
 * были персональные данные, идёт только к тем, чей retention нам известен.
 * Лучше отказ, чем тихая отправка в маршрут, про который мы ничего не знаем.
 */
export function providersFor(
  config: RuntimeConfig,
  scenario: ScenarioId,
  opts: { personalData?: boolean } = {},
): ProviderId[] {
  if (!config.scenarios[scenario]?.enabled) return []
  return (config.routes[scenario] ?? [])
    .filter((p) => config.providers[p]?.enabled)
    .filter((p) => !opts.personalData || config.providers[p]?.personalData === true)
}

/** Наложение отклонений из базы на значения по умолчанию. */
export function mergeConfig(base: RuntimeConfig, overrides: unknown): RuntimeConfig {
  if (overrides == null || typeof overrides !== 'object') return base
  const o = overrides as Partial<RuntimeConfig>
  return {
    providers: { ...base.providers, ...(o.providers ?? {}) },
    models: { ...base.models, ...(o.models ?? {}) },
    scenarios: { ...base.scenarios, ...(o.scenarios ?? {}) },
    routes: { ...base.routes, ...(o.routes ?? {}) },
    // Слияние на уровень глубже, чем у соседей: `units` — не плоская запись, а
    // независимые таблицы. Отклонение по лимитам не должно уносить с собой
    // цены пакетов, и наоборот.
    units: {
      limits: { ...base.units.limits, ...(o.units?.limits ?? {}) },
      packs: o.units?.packs ?? base.units.packs,
      warnAtShare: o.units?.warnAtShare ?? base.units.warnAtShare,
    },
    scenarioUnits: { ...base.scenarioUnits, ...(o.scenarioUnits ?? {}) },
    scenarioScope: { ...base.scenarioScope, ...(o.scenarioScope ?? {}) },
    web: { ...base.web, ...(o.web ?? {}) },
    freeEdits: { ...base.freeEdits, ...(o.freeEdits ?? {}) },
    ratings: { ...base.ratings, ...(o.ratings ?? {}) },
    recipes: { ...base.recipes, ...(o.recipes ?? {}) },
    topics: { ...base.topics, ...(o.topics ?? {}) },
    guest: { ...base.guest, ...(o.guest ?? {}) },
    budgets: {
      perScenario: { ...base.budgets.perScenario, ...(o.budgets?.perScenario ?? {}) },
    },
  }
}

/** Потолок для сценария. Отсутствие потолка — это ошибка конфигурации, не ноль. */
export const budgetFor = (config: RuntimeConfig, scenario: ScenarioId): number =>
  config.budgets.perScenario[scenario] ?? 0

// Дальше — доступ к значениям. Аргументом идёт не вся конфигурация, а ровно
// та её часть, которую функция читает: так вызывающему видно, что она может
// испортить, а тесту не приходится собирать целый RuntimeConfig ради одного
// числа.

/** Сколько единиц в месяц даёт тариф. */
export const limitFor = (config: Pick<RuntimeConfig, 'units'>, tariff: TariffId, unit: UnitId): number =>
  config.units.limits[tariff]?.[unit] ?? 0

/**
 * Сколько единиц даёт гостевой аккаунт. SMY-160
 *
 * Доля от БЕСПЛАТНОГО, а не своя таблица: разойтись с сеткой оно не должно
 * даже при полной её пересборке из админки.
 *
 * ⚠️ ВНИЗ, НО НЕ НИЖЕ ЕДИНИЦЫ, пока у тарифа единица вообще есть. Половина от
 * пяти — это два, а не два с половиной; но половина от единицы — это единица,
 * а не ноль. Ноль убирает сценарий с главного экрана целиком, и человек не
 * понимает, чего он лишён и что с этим делать: «попробовать нельзя» выглядит
 * поломкой, а не приглашением зарегистрироваться.
 *
 * Ноль у самого тарифа остаётся нулём: там единица недоступна по решению, и
 * выдавать её гостю значило бы дать ему то, чего нет у зарегистрированного.
 */
export function guestLimitFor(
  config: Pick<RuntimeConfig, 'units' | 'guest'>,
  unit: UnitId,
): number {
  const full = limitFor(config, 'free', unit)
  if (full <= 0) return 0
  return Math.max(1, Math.floor(full * config.guest.limitShare))
}

/**
 * Потолок аккаунта: тарифный у зарегистрированного, гостевой у остальных.
 *
 * ⚠️ ПРИЗНАК — «ПОЧТА ПОДТВЕРЖДЕНА», а не «почта есть». Иначе половинные
 * лимиты снимались бы вводом чужого адреса, до которого не дошло ни одного
 * письма (SMY-152).
 *
 * Тариф у гостя при этом обычный `free`: гость — состояние аккаунта, а не
 * пятый тариф. Пятый потребовал бы миграции `pgEnum`, места в `TARIFF_RANK`,
 * строки в прайсе, пункта в выдаче из админки и разреза в аналитике — везде,
 * где гостю делать нечего.
 */
export const limitForAccount = (
  config: Pick<RuntimeConfig, 'units' | 'guest'>,
  tariff: TariffId,
  unit: UnitId,
  registered: boolean,
): number => (registered ? limitFor(config, tariff, unit) : guestLimitFor(config, unit))

/** Сколько правок результата бесплатны и как долго. */
export const editPolicyFor = (
  config: Pick<RuntimeConfig, 'freeEdits'>,
  scenario: ScenarioId,
): EditPolicy => config.freeEdits[scenario] ?? DEFAULT_CONFIG.freeEdits[scenario]

/** Каким получится результат сценария: сколько ответов, страниц, вариантов. */
export const scopeFor = (
  config: Pick<RuntimeConfig, 'scenarioScope'>,
  scenario: ScenarioId,
): UnitScope => config.scenarioScope[scenario] ?? DEFAULT_CONFIG.scenarioScope[scenario]

/**
 * Ставить ли рецепт типу задачи. Нет раздела в конфигурации (строка из базы
 * старше SMY-496) — умолчание, то есть выключено.
 */
export const recipeOn = (config: Pick<RuntimeConfig, 'recipes'>, type: TaskTypeId): boolean => {
  const recipes = config.recipes ?? DEFAULT_CONFIG.recipes
  return recipes.enabled && !recipes.off.includes(type)
}

/**
 * Выключатель после команды. Без типа — общий флаг, список снятых не трогается;
 * с типом — только его строка в `off`: включение типа общего флага не
 * включает, выключение типа его не выключает.
 */
export function switchRecipes(recipes: RecipesConfig, on: boolean, type?: TaskTypeId): RecipesConfig {
  if (type == null) return { ...recipes, enabled: on }
  const off = recipes.off.filter((t) => t !== type)
  return { ...recipes, off: on ? off : [...off, type] }
}

/** Сколько источников читает веб-поиск внутри ответа. */
export const webSourcesFor = (config: Pick<RuntimeConfig, 'web'>): number =>
  config.web?.sources ?? DEFAULT_CONFIG.web.sources

/** Чем списывается сценарий. */
export const unitForScenario = (
  config: Pick<RuntimeConfig, 'scenarioUnits'>,
  scenario: ScenarioId,
): UnitId => config.scenarioUnits[scenario] ?? DEFAULT_CONFIG.scenarioUnits[scenario]

/**
 * Доступна ли единица на тарифе. Нулевой лимит — это «не входит в тариф», а
 * не «кончилось»: экран 02 гасит карточку, а не предлагает докупить.
 */
export const isUnitAvailable = (
  config: Pick<RuntimeConfig, 'units'>,
  tariff: TariffId,
  unit: UnitId,
): boolean => limitFor(config, tariff, unit) > 0
